import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Mail, CheckCircle, RefreshCcw, LogOut } from 'lucide-react';

const VerifyEmail = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  
  const { user, verifyEmail, resendVerification, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyEmail(verificationCode);
      toast.success(language === 'de' ? 'E-Mail erfolgreich verifiziert!' : 'Email successfully verified!');
      navigate('/pending-confirmation');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'de' ? 'Verifizierung fehlgeschlagen' : 'Verification failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const result = await resendVerification();
      toast.success(language === 'de' 
        ? `Neuer Code wurde gesendet! (Demo Code: ${result.verification_code})` 
        : `New code sent! (Demo Code: ${result.verification_code})`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'de' ? 'Fehler beim Senden' : 'Failed to resend'));
    } finally {
      setResending(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (user.email_verified) {
    navigate('/markets');
    return null;
  }

  return (
    <div className="auth-container">
      <Card className="auth-card border-none sm:border-solid shadow-none sm:shadow-md">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-red-500/10 animate-pulse">
              <Mail className="w-12 h-12 text-red-500" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">
            {language === 'de' ? 'E-Mail verifizieren' : 'Verify Email'}
          </CardTitle>
          <CardDescription className="text-sm">
            {language === 'de' 
              ? `Wir haben einen Code an ${user.email} gesendet.`
              : `We've sent a code to ${user.email}.`
            }
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleVerify}>
          <CardContent className="space-y-6 pt-4">
            <div className="space-y-2">
              <Label className="text-center block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {language === 'de' ? 'Verifizierungscode' : 'Verification Code'}
              </Label>
              <Input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                maxLength={6}
                className="h-16 text-center text-3xl font-mono font-bold tracking-[0.5em] rounded-xl border-2 focus:border-red-500/50"
                required
                autoFocus
              />
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pt-2">
            <Button 
              type="submit" 
              className="w-full bg-red-600 hover:bg-red-700 h-12 text-base font-bold rounded-xl shadow-lg shadow-red-600/20" 
              disabled={loading}
            >
              {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              {language === 'de' ? 'E-Mail bestätigen' : 'Confirm Email'}
            </Button>
            
            <div className="flex gap-2 w-full">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1 h-11 rounded-xl" 
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                {language === 'de' ? 'Neuer Code' : 'Resend'}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                className="flex-1 h-11 rounded-xl text-muted-foreground" 
                onClick={logout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {language === 'de' ? 'Abmelden' : 'Logout'}
              </Button>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default VerifyEmail;
